import { FC, useEffect, useState } from 'react';

import { Link } from 'react-router-dom';

import { BugoStatFevent } from './adminBugoStat.types';
import {
  acqMsgCountOf,
  brandNameOfFevent,
  bugoSentCountOf,
  coffinOutDayKey,
  funeralHomeNameOf,
} from './adminBugoStat.util';
import { useAnalyticsBrandNames } from './useAnalyticsBrandNames';

//* 표/달력에서 "이 칸의 N건이 뭐였지"를 열어보는 드릴다운.
//* @shared의 openId 모달은 닫을 때 history.back()을 해서 통계 화면의 필터 상태와 얽힌다 —
//* 여기서는 로컬 state로만 여닫는 오버레이를 쓴다 (giftSeller 드릴다운과 같은 방식).

//* 한 번에 그리는 최대 건수 — 반기 조회는 상가가 수천~2만 건이라 전량 렌더하면 화면이 멎는다
const PAGE_SIZE = 500;

const shareCountOf = (fevent: BugoStatFevent): number => {
  const raw = fevent.meta?.shareCount;
  return typeof raw === 'number' && raw > 0 ? raw : 0;
};

export const AdminBugoFeventListModal: FC<{
  title: string;
  subtitle?: string;
  fevents: BugoStatFevent[];
  /** bugos populate 실패로 발송 수치를 못 쓰는 상태 */
  bugoDataUnavailable: boolean;
  /** acqMsgs populate 실패로 조의글 개수를 못 쓰는 상태 */
  acqMsgDataUnavailable: boolean;
  onClose: () => void;
}> = function AdminBugoFeventListModal({
  title,
  subtitle,
  fevents,
  bugoDataUnavailable,
  acqMsgDataUnavailable,
  onClose,
}) {
  //* 상조명은 이름 맵으로 붙인다 (문서에 populate가 없다). 맵은 세션 캐시라 추가 조회 없음
  const { resolveBrandName } = useAnalyticsBrandNames();

  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  //* 같은 모달이 열린 채 다른 칸으로 바뀌어도 처음부터 다시 세도록
  useEffect(() => setVisibleCount(PAGE_SIZE), [fevents]);
  const visibleFevents = fevents.slice(0, visibleCount);
  const restCount = fevents.length - visibleFevents.length;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-3">
      <div
        className="fixed inset-0 bg-black/50"
        onClick={onClose}
        aria-hidden="true"
      />
      <div className="relative my-3 flex max-h-[92vh] w-full max-w-[900px] flex-col overflow-hidden rounded-xl bg-white shadow-2xl">
        <div className="flex items-start justify-between border-b border-gray-200 px-4 py-3">
          <div className="min-w-0">
            <p className="truncate text-18 font-bold text-gray-900">{title}</p>
            {/* 한 문자열로 묶어 낸다 — 텍스트 노드가 쪼개지면 읽기도 검사도 불편하다 */}
            <p className="mt-0.5 text-13 tabular-nums text-gray-600">
              {`${subtitle ? `${subtitle} · ` : ''}${fevents.length.toLocaleString()}건`}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="닫기"
            className="ml-3 shrink-0 rounded-lg px-2.5 py-1 text-20 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
          >
            ✕
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-auto bg-gray-50 p-3">
          {fevents.length === 0 ? (
            <p className="py-10 text-center text-14 text-gray-500">
              해당 조건의 상가가 없습니다.
            </p>
          ) : (
            <ul className="space-y-1.5">
              {visibleFevents.map((fevent) => {
                const bugo = bugoSentCountOf(fevent);
                const shareCount = shareCountOf(fevent);
                const funeralHome = funeralHomeNameOf(fevent);
                const acqMsgCount = acqMsgCountOf(fevent);
                const room = fevent.roomInfoEmbed?.name;
                return (
                  <li
                    key={fevent._id}
                    className="rounded-lg bg-white px-3 py-2 shadow-smd"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-16 font-semibold text-gray-900">
                          <span className="tabular-nums text-gray-500">
                            {coffinOutDayKey(fevent) ?? '발인일 없음'}
                          </span>
                          <span className="ml-2">
                            {fevent.deceasedInfo?.name || '(고인명 없음)'}
                          </span>
                        </p>
                        <p className="mt-0.5 truncate text-13 text-gray-600">
                          {brandNameOfFevent(fevent, resolveBrandName)}
                          {funeralHome ? ` · ${funeralHome}` : ''}
                          {room ? ` ${room}` : ''}
                        </p>
                      </div>
                      {/* 관리자용 부고정보 — 상주·주문까지 여기서 본다 */}
                      <Link
                        to={`/fevent/${fevent._id}/admin-info`}
                        className="shrink-0 rounded-lg bg-gray-100 px-3 py-1.5 text-13 font-medium text-gray-700 hover:bg-gray-200"
                      >
                        상세
                      </Link>
                    </div>

                    <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5 text-13 tabular-nums">
                      <span
                        className={
                          shareCount > 0 ? 'text-emerald-700' : 'text-gray-400'
                        }
                      >
                        공유 {shareCount}
                      </span>
                      {/* 설정 on/off와 실제로 달린 글 수는 다른 값이라 같이 보여준다 */}
                      <span
                        className={
                          fevent.useAcqMsg ? 'text-indigo-700' : 'text-gray-400'
                        }
                      >
                        조의글 {fevent.useAcqMsg ? '설정' : '미설정'}
                        {!acqMsgDataUnavailable && ` ${acqMsgCount}개`}
                      </span>
                      {bugoDataUnavailable ? (
                        <span className="text-gray-400">부고 정보 없음</span>
                      ) : (
                        <span
                          className={
                            bugo.total > 0 && bugo.sent === 0
                              ? 'text-orange-600'
                              : 'text-gray-500'
                          }
                        >
                          부고 {bugo.total} · 발송 {bugo.sent}
                        </span>
                      )}
                      {fevent.registerNumber && (
                        <span className="text-gray-400">
                          접수 {fevent.registerNumber}
                        </span>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
          {restCount > 0 && (
            <button
              type="button"
              onClick={() => setVisibleCount((prev) => prev + PAGE_SIZE)}
              className="mt-2 w-full rounded-lg bg-white py-2.5 text-14 font-medium text-gray-700 shadow-smd hover:bg-gray-100"
            >
              {`더보기 (${restCount.toLocaleString()}건 남음)`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
